/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { requestCommon } from 'live/lib/utils'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import xhr from 'live/services/xhr'
import { KpiTypeMap } from '../dashboardFilters/types'
import { fetchKpiData, fetchKpiDataForAsset } from '../kpiConfiguration/api'
import { Channel, KPI, StandardChannel, StandardRigChannel, StandEvent } from '../widgets/annotations/utils/types'
import { GoalsVersioningResponse } from '../types/types'

type KpiDataType = {
    begin: number
    end: number
}

type newKpiDataType = {
    indicator: string
    begin: number
    end: number
}

type FetchResponseInterface = {
    json: () => Promise<updateKPIPromiseResponse>
    status: number
    message?: string
}

type updateKPIPromiseResponse = {
    sucess: string
    failure: string
}

function transformRigstateChannelsToMapIdentifierMnemonic(rigstateChannels: Map<StandardRigChannel, Channel>) {
    const obj = {}

    const isMap = rigstateChannels?.constructor?.name === 'Map'
    const isObject = rigstateChannels?.constructor?.name === 'Object'

    if (isMap) rigstateChannels?.forEach((v, k) => (obj[k?.identifier] = v?.mnemonic))

    if (!isMap && isObject) {
        Object.keys(rigstateChannels)?.forEach(
            k => (obj[rigstateChannels[k]?.identifier] = rigstateChannels[k]?.mnemonic)
        )
    }

    return obj
}
export type FetchStatus = 'loading' | 'OK' | 'error'

export function fetchRealTimeDataAggregation(assetType: string, assetId: string): Promise<any> {
    return fetch(`/services/plugin-asset-aggregator/aggregations/${assetType}/${assetId}/status`).then(r =>
        r
            .json()
            .then(data => ({ status: r.status, body: data }))
            .then(res => {
                if (res.status !== 200) {
                    globalNotification.error('Error getting Realtime status in Data Aggregation.')
                }

                return res.body.real_time_compression_query
            })
            .catch(e => console.error(e))
    )
}

export function fetchIdentifiers(): Promise<any> {
    return fetch(`/services/plugin-liverig/identifier/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
}

export function fetchRigs(): Promise<any> {
    return fetchAssets('rig')
}

export function fetchWells(): Promise<any> {
    return fetchAssets('well')
}

export function fetchAssetEventType(assetType: string, assetId: string): Promise<any> {
    return fetch(`/services/plugin-liverig/assets/${assetType}/${assetId}`, {
        ...requestCommon,
        method: 'GET'
    }).then(response => {
        return response
    })
}

export function fetchAssets(assetType: string, flags?: string[]): Promise<any> {
    return fetch(`/services/plugin-liverig/assets/${assetType}/search?`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flags })
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
}

export function fetchGlobalInterpolationParameters(): Promise<any> {
    return fetch(`/services/plugin-kpis-drilling/kpi/interpolation-parameters/get-interpolation-parameters/GLOBAL`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .catch(r => ({ body: undefined }))
}

export function fetchInterpolationParameters(assetId: string): Promise<any> {
    return fetch(
        `/services/plugin-kpis-drilling/kpi/interpolation-parameters/get-interpolation-parameters/${assetId}`,
        {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        }
    )
        .then(r =>
            r.bodyUsed
                ? fetchGlobalInterpolationParameters()
                : r.json().then(data => ({ status: r.status, body: data }))
        )
        .then(res => {
            return res.body
        })
        .catch(r => fetchGlobalInterpolationParameters().then(r => r.body))
}

export function fetchCurves(): Promise<any> {
    return fetch(`/services/plugin-liverig/curves/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
}

export async function fetchNormalizer(assetType: string, assetId: string): Promise<any> {
    const r = await fetch(`/services/plugin-liverig/assets/${assetType}/${assetId}/normalizer`)
    if (!r.ok) {
        console.error(
            `Error getting normalizer configurations for ${assetType} with ID - request status: '${r.statusText}'`
        )
    } else {
        const json = await r.json()

        return json
    }
}

export function fetchKPIs(): Array<KPI> | any {
    return fetch(`/services/plugin-kpis-drilling/config/kpi_colors`)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            const result = res.body
            if (!result || !result.lastUpdated) {
                globalNotification.error(`${i18n(`Something went wrong`)}: ${result.body.message} - ${res.status}`, 10)

                return null
            }
            const kpisResponse = result.data.map(kpiData => ({
                identifier: kpiData.key,
                label: kpiData.title,
                color: kpiData.value
            }))

            return kpisResponse
        })
}

interface createStandEventProps {
    event_type: string
    rig_id: string
    rig_name: string | undefined
    well_id: string | undefined
    well_name: string | undefined
    stand_start_time: number
    stand_end_time: number
    bit_depth_uom?: string
    start_bit_depth?: number
    end_bit_depth?: number
    hole_depth_uom?: string
    start_hole_depth?: number
    end_hole_depth?: number
    stand_status?: string
    stand_type?: string
    stand_size?: string
    channels?: Map<StandardRigChannel, Channel>
}
export function createStandEvent({
    event_type,
    stand_start_time,
    stand_end_time,
    bit_depth_uom,
    start_bit_depth,
    end_bit_depth,
    hole_depth_uom,
    start_hole_depth,
    end_hole_depth,
    stand_status,
    stand_type,
    stand_size,
    rig_id,
    rig_name,
    well_id,
    well_name,
    channels
}: createStandEventProps): Promise<FetchResponseInterface> {
    const createStringfyedBody = JSON.stringify({
        event_type: event_type,
        stand_start_time: stand_start_time,
        stand_end_time: stand_end_time,
        rig_id: rig_id,
        rig_name: rig_name,
        well_id: well_id,
        well_name: well_name,

        bit_depth_uom,
        start_bit_depth,
        end_bit_depth,
        hole_depth_uom,
        start_hole_depth,
        end_hole_depth,
        stand_status,
        stand_type,
        stand_size,
        channels: channels ? transformRigstateChannelsToMapIdentifierMnemonic(channels) : null
    })

    return fetch(`/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/create-stand`, {
        ...requestCommon,
        method: 'POST',
        body: createStringfyedBody
    }).then(response => {
        return response
    })
}

type StandSearchResponseInterface = {
    json: () => Promise<searchStandPromiseResponse>
    status: number
    message?: string
}

type searchStandPromiseResponse = {
    success: StandEvent
    failure: string
}

export function searchStandEvent(
    eventType: string,
    standStartTime: number,
    standEndTime: number
): Promise<StandSearchResponseInterface> {
    const createStringfyedBody = JSON.stringify({
        event_type: eventType,
        stand_start_time: standStartTime,
        stand_end_time: standEndTime
    })

    return fetch(`/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/search-stand`, {
        ...requestCommon,
        method: 'POST',
        body: createStringfyedBody
    }).then(response => {
        return response
    })
}

interface EditStandCountProps {
    previous_stand: {} | undefined
    next_stand: {} | undefined
    stand_id: number | string
    event_type: string
    rig_id: string
    rig_name: string | undefined
    well_id: string | undefined
    well_name: string | undefined
    stand_start_time: number
    stand_end_time: number
    bit_depth_uom?: string
    start_bit_depth?: number
    end_bit_depth?: number
    hole_depth_uom?: string
    start_hole_depth?: number
    end_hole_depth?: number
    stand_status?: string
    stand_type?: string
    stand_size?: string
    channels?: Map<StandardRigChannel, Channel>
}
export function editStandCountEvent({
    previous_stand,
    next_stand,
    stand_id,
    event_type,
    stand_start_time,
    stand_end_time,
    bit_depth_uom,
    start_bit_depth,
    end_bit_depth,
    hole_depth_uom,
    start_hole_depth,
    end_hole_depth,
    stand_status,
    stand_type,
    stand_size,
    rig_id,
    rig_name,
    well_id,
    well_name,
    channels
}: EditStandCountProps): Promise<FetchResponseInterface> {
    const createStringfyedBody = JSON.stringify({
        stand_id: stand_id,
        event_type: event_type,
        stand_start_time,
        stand_end_time,
        rig_id: rig_id,
        rig_name: rig_name,
        well_id: well_id,
        well_name: well_name,

        previous_stand,
        next_stand,
        bit_depth_uom,
        start_bit_depth,
        end_bit_depth,
        hole_depth_uom,
        start_hole_depth,
        end_hole_depth,
        stand_status,
        stand_type,
        stand_size,
        channels: channels ? transformRigstateChannelsToMapIdentifierMnemonic(channels) : null
    })

    return fetch(`/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/update-stand`, {
        ...requestCommon,
        method: 'POST',
        body: createStringfyedBody
    }).then(response => {
        return response
    })
}

export function deleteStandCountEvent(eventType: string, standId: string): Promise<FetchResponseInterface> {
    const createStringfyedBody = JSON.stringify({
        event_type: eventType,
        stand_id: standId
    })

    return fetch(`/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/delete-stand`, {
        ...requestCommon,
        method: 'POST',
        body: createStringfyedBody
    }).then(response => {
        return response
    })
}

export interface CreateKPIRequest {
    event_type: string
    new_kpi_data: newKpiDataType
    channels: Map<StandardChannel, Channel>
    rig_id: string
    well_id: string | undefined
    rig_name: string | undefined
    well_name: string | undefined
}
export function createKPIEvent({
    event_type,
    new_kpi_data,
    channels,
    rig_id,
    well_id,
    rig_name,
    well_name
}: CreateKPIRequest): Promise<FetchResponseInterface> {
    const createStringfyedBody = JSON.stringify({
        data: new_kpi_data,
        channels: transformRigstateChannelsToMapIdentifierMnemonic(channels),
        event_type,
        rig_id,
        well_id,
        rig_name,
        well_name
    })

    return fetch(`/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/create-kpi`, {
        ...requestCommon,
        method: 'POST',
        body: createStringfyedBody
    }).then(response => {
        return response
    })
}

export interface UpdateKPIRequest {
    event_type: string
    uid: number
    new_kpi_data: KpiDataType
    channels: Map<StandardChannel, Channel>
    rig_id: string
    well_id: string | undefined
    rig_name: string | undefined
    well_name: string | undefined
}
export function updateKPIAfterDrag({
    event_type,
    uid,
    new_kpi_data,
    channels,
    rig_id,
    well_id,
    rig_name,
    well_name
}: UpdateKPIRequest): Promise<FetchResponseInterface> {
    const stringfyedBody = JSON.stringify({
        event_type,
        uid: uid,
        data: new_kpi_data,
        channels: channels ? transformRigstateChannelsToMapIdentifierMnemonic(channels) : null,
        rig_id,
        well_id,
        rig_name,
        well_name
    })

    return fetch(`services/plugin-kpi-core/kpi/call/kpi-drilling/v1/update-kpi`, {
        ...requestCommon,
        method: 'POST',
        body: stringfyedBody
    }).then(response => {
        return response
    })
}

type deleteKPIFunctionInterface = {
    status: number
}

export function handleDeleteKpiButtonClick(eventType: string, uid: string): Promise<deleteKPIFunctionInterface> {
    const stringfyedBody = JSON.stringify({
        event_type: eventType,
        uid: uid
    })

    return fetch(`services/plugin-kpi-core/kpi/call/kpi-drilling/v1/delete-kpi`, {
        ...requestCommon,
        method: 'POST',
        body: stringfyedBody
    }).then(response => {
        return response
    })
}

interface updateReprocessedProperties {
    begin: number
    end: number
    status: string
}

export async function updateReprocessedEvent(
    event: string,
    properties: updateReprocessedProperties,
    newStatus: string
): Promise<deleteKPIFunctionInterface> {
    const stringfyedBody = JSON.stringify({
        event,
        properties,
        data: {
            status: newStatus
        }
    })

    const response = await fetch(`services/plugin-kpi-core/kpi/call/kpi-drilling/v1/update-reprocess-event`, {
        ...requestCommon,
        method: 'POST',
        body: stringfyedBody
    })
    const responseJson = await response.json()

    return responseJson
}

export interface Entry {
    key: string
    value: string
}

export interface LookupTableResponse {
    data?: {
        message?: string
        active?: boolean
        editable?: boolean
        entries?: Array<Entry>
        id?: string
        name?: string
        source?: string
        sourceUrl?: string
    }
    config: { method: string }
    status: 200 | 404 | number
}

export async function getLookupTable(macro: string): Promise<LookupTableResponse> {
    const response = await xhr
        .get(`rest/lookup/${macro}`)
        .then((response: LookupTableResponse) => response)
        .catch((response: LookupTableResponse) => response)

    return response
}

export async function fetchSelectedKpiParameters(args: { dashboardId: string }): Promise<Response> {
    const response = await fetch(`/services/plugin-kpis-drilling/kpi/kpi-parameters/get-selected-kpi-parameters`, {
        ...requestCommon,
        method: 'POST',
        body: JSON.stringify(args)
    })

    return response
}

export async function saveSelectedKpiParameters(args: {
    dashboardId: string
    userKpiParameters: KpiTypeMap
}): Promise<Response> {
    const response = await fetch(`/services/plugin-kpis-drilling/kpi/kpi-parameters/save-kpi-parameters`, {
        ...requestCommon,
        method: 'POST',
        body: JSON.stringify(args)
    })

    return response
}

export interface FleetOverviewFilters {
    selectedRig?: string[]
    selectedWell?: string[]
    pathFilter?: string
    dataQualityFilter?: string
    timespan?: string
}

export async function fetchFleetOverviewFilters(): Promise<FleetOverviewFilters> {
    const response = await fetch(`/services/plugin-kpis-drilling/fleet-overview/filters`, {
        ...requestCommon,
        method: 'GET'
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch Fleet Overview filters (status ${response.status})`)
    }

    return response.json()
}

export async function saveFleetOverviewFilters(filters: FleetOverviewFilters, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`/services/plugin-kpis-drilling/fleet-overview/filters`, {
        ...requestCommon,
        method: 'POST',
        body: JSON.stringify(filters),
        signal
    })

    if (!response.ok) {
        throw new Error(`Failed to save Fleet Overview filters (status ${response.status})`)
    }
}

interface simulateCreateStandEventProps {
    event_type: string
    rig_id: string
    rig_name: string | undefined
    well_id: string | undefined
    well_name: string | undefined
    stand_start_time: number
    stand_end_time: number
    channels?: Map<StandardRigChannel, Channel>
}
export async function simulateStandCreation({
    event_type,
    stand_start_time,
    stand_end_time,
    rig_id,
    rig_name,
    well_id,
    well_name,
    channels
}: simulateCreateStandEventProps): Promise<any> {
    const response = await fetch(`/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/simulate-create-stand`, {
        ...requestCommon,
        method: 'POST',
        body: JSON.stringify({
            event_type: event_type,
            stand_start_time: stand_start_time,
            stand_end_time: stand_end_time,
            rig_id: rig_id,
            rig_name: rig_name,
            well_id: well_id,
            well_name: well_name,
            channels: channels ? transformRigstateChannelsToMapIdentifierMnemonic(channels) : null
        })
    })
    if (!response.ok) return {}

    const json = await response.json()

    return json.success ? json.success : {}
}

interface RealtimeConnectionsList {
    lastUpdated: number
    author: string
    data: {
        connections: unknown
        help: string
    }
}

async function getRealtimeConnectionsList(): Promise<RealtimeConnectionsList> {
    const url = `/services/plugin-kpis-drilling/config/realtime`

    const r = await fetch(url)
    const response = await r.json()

    return response
}
export const RigStateChannels = async (
    assetType: string,
    assetId: string,
    stackColors: string[] = []
): Promise<Map<StandardRigChannel, Channel>> => {
    let rigstateConfig: any

    // case well
    if (assetType == 'well') {
        const realtimeConnections = await getRealtimeConnectionsList()
        const realtimeConnectionsList = realtimeConnections?.data?.connections
        let rigConnectToWellId = null
        Object.entries(realtimeConnectionsList ?? {}).forEach(entry => {
            if (entry[1] === assetId) rigConnectToWellId = entry[0]
        })
        if (!rigConnectToWellId) {
            rigstateConfig = await fetchKpiData('rigstate_channels')
        } else {
            try {
                rigstateConfig = await fetchKpiDataForAsset('rigstate_channels', rigConnectToWellId)
            } catch (err) {
                rigstateConfig = await fetchKpiData('rigstate_channels')
            }
        }
    } else {
        try {
            rigstateConfig = await fetchKpiDataForAsset('rigstate_channels', assetId)
        } catch (err) {
            rigstateConfig = await fetchKpiData('rigstate_channels')
        }
    }

    // case rig

    const configChannels = rigstateConfig.data.channels
    const rigStateIdentifier: any = await fetchIdentifiers()
    const channels: StandardRigChannel[] = Object.keys(rigStateIdentifier).map(key => {
        const channel = rigStateIdentifier[key]

        return {
            identifier: key,
            mnemonic: channel.mnemonic,
            name: channel.name,
            // Is this below needed??
            isRigStateChannel: true
        }
    })

    const rigstateChannelsList = []

    const rigStateCurves: any = await fetchCurves()
    Object.entries(configChannels).forEach((entry: any) => {
        // entry[0] is the identifier of the original channel
        // entry[1] the alternative channels object is the,  .mnemonic of the selected alternative channel
        const rigStateChannel = channels.find(ch => ch.identifier == entry[0])
        const selectedChannel = rigStateCurves[entry[1].mnemonic as string]
        const channelColor: string = selectedChannel?.color || stackColors?.pop()
        const mappedChannel: Channel = {
            mnemonic: entry[1].mnemonic as string,
            color: channelColor,
            name: selectedChannel?.description,
            unit: selectedChannel?.unit
        }

        rigstateChannelsList.push([rigStateChannel, mappedChannel])
    })

    const rigStateChannelsMap = new Map(rigstateChannelsList) as Map<StandardRigChannel, Channel>

    return rigStateChannelsMap
}

export function fetchGoalsVersioningByAsset(body: {
    assetType: 'WELL' | 'RIG' | 'GLOBAL'
    assetId?: string
}): Promise<GoalsVersioningResponse[]> {
    return fetch(`/services/plugin-kpis-drilling/goals_versioning/search`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(r =>
            r.json().then(data => {
                if (r.status != 200) {
                    throw new Error(data?.message)
                }

                return { status: r.status, body: data }
            })
        )
        .then(res => {
            return res.body
        })
}

const BASE_LIVE_DASHBOARDS_URL = '/rest/dashboard'
const BASE_LIVE_PLUGINS_URL = '/rest/plugin'
const BASE_STATIC_DATA_MANAGER_URL = '/services/plugin-static-data-manager'

export interface LiveDashboardSummary {
    id: number | string
    title: string
    url?: string
}

export async function fetchDashboards(): Promise<LiveDashboardSummary[]> {
    const response = await xhr.get(BASE_LIVE_DASHBOARDS_URL)

    return response?.data?.data ?? []
}

export async function findDashboardByTitle(dashboardTitle: string): Promise<LiveDashboardSummary | undefined> {
    const dashboards = await fetchDashboards()

    return dashboards.find((dashboard: LiveDashboardSummary) => dashboard.title === dashboardTitle)
}

export async function getLinkToDashboard(dashboardTitle: string): Promise<string | undefined> {
    const dashboard = await findDashboardByTitle(dashboardTitle)

    return dashboard?.url
}

export interface StaticDataIDSResults {
    header: {
        rig: string
        well: string
        consistency: number
        kpiTotalOperationTime: number
        rpiCoveragePercentage: {
            unit: string
            value: number
        }
        rpiRelevantPath: string
        rigResponsible: string[]
        rigs: string[]
        totalOperationTime: {
            unit: string
            value: number
        }
    }
    operationRows: Array<{
        measured: {
            unit: string
            value: number
        }
        rpiCoveragePercentage: {
            unit: string
            value: number
        }
        gainsOverGoal: {
            unit: string
            value: number
        }
        planned: {
            unit: string
            value: number
        }
        iltOverGoal: {
            unit: string
            value: number
        }
        kpiGoal: {
            unit: string
            value: number
        }
        totalOperationTime: {
            unit: string
            value: number
        }
        operationType: string
        potentialSavingsOverGoal: {
            unit: string
            value: number
        }
        kpis: any[]
        goalsVersionUUID: string[]
    }>
    resultLog: string
    timespanRows: Array<{
        measured: Record<string, any>
        iltOverGoal: Record<string, any>
        gainsOverGoal: Record<string, any>
        potentialSavingsOverGoal: Record<string, any>
        timespan: string
        planned: Record<string, any>
    }>
}

export function getIDSResults(
    start: string,
    end: string,
    mainRigId: string,
    auxRigId?: string,
    rigTableInfoFilter?: string,
    dataQuality?: string
): Promise<StaticDataIDSResults> {
    const params = new URLSearchParams({ start, end })
    if (rigTableInfoFilter) params.set('rigTableInfoFilter', rigTableInfoFilter)
    if (dataQuality) params.set('dataQuality', dataQuality)

    return xhr
        .get(
            BASE_STATIC_DATA_MANAGER_URL +
                '/kpidatapersistence/getIDSResults/rig/' +
                mainRigId +
                (auxRigId ? ',' + auxRigId : '') +
                '/?' +
                params.toString()
        )
        .then(response => response?.data)
}

interface LivePluginStatusResponse {
    name: string
    exists: boolean
    active: boolean
    valid: boolean
}

interface LivePluginEntry {
    name: string
    active?: boolean
    status?: {
        valid?: boolean
    }
}

export function getLivePluginsInfo(pluginNames: string | string[]): Promise<LivePluginStatusResponse[]> {
    const names = (Array.isArray(pluginNames) ? pluginNames : [pluginNames])
        .map(name => (name || '').trim())
        .filter(Boolean)

    return xhr.get(BASE_LIVE_PLUGINS_URL).then(response => {
        const plugins: LivePluginEntry[] = response?.data?.data ?? []

        return names.map(name => {
            const plugin = plugins.find(p => p?.name === name)

            return {
                name,
                exists: !!plugin,
                active: !!plugin?.active,
                valid: !!plugin?.status?.valid
            }
        })
    })
}
